import m from 'mithril';
import '@awesome.me/webawesome/dist/components/button/button.js';

import type { MithrilViewEvent } from '../types';

/**
 * Button click event including Mithril redraw control.
 */
export type ButtonClickEvent = MouseEvent & {
  /** Set to false to prevent Mithril's automatic redraw. */
  redraw?: boolean;
};

/**
 * MeshOS button attrs backed by Webawesome.
 */
export interface ButtonAttrs extends m.Attributes {
  /** Button visual priority. */
  variant?: 'brand' | 'neutral' | 'success' | 'warning' | 'danger';
  /** Button appearance. */
  appearance?: 'accent' | 'filled' | 'outlined' | 'plain' | 'filled-outlined';
  /** Button size. */
  size?: 's' | 'm' | 'l' | 'xl';
  /** Native button type. */
  type?: 'button' | 'submit' | 'reset';
  /** Link target when rendering as an anchor. */
  href?: string;
  /** Link target mode. */
  target?: string;
  /** Link rel attribute. */
  rel?: string;
  /** Download filename hint. */
  download?: string;
  /** Whether the button is disabled. */
  disabled?: boolean;
  /** Whether the button is loading. */
  loading?: boolean;
  /** Whether the button should fill its container. */
  full?: boolean;
  /** Additional CSS class names. */
  className?: string;
  /** Click handler. */
  onclick?: (event: ButtonClickEvent) => void;
}

/**
 * Renders a standard MeshOS button.
 */
export const Button: m.Component<ButtonAttrs> = {
  view: ({ attrs, children }) => {
    const classes = [
      'button',
      attrs.full ? 'button--full' : '',
      attrs.className ?? '',
    ].filter(Boolean).join(' ');

    return m('wa-button', {
      class: classes,
      variant: attrs.variant ?? 'brand',
      appearance: attrs.appearance ?? 'accent',
      size: attrs.size ?? 'm',
      type: attrs.type ?? 'button',
      href: attrs.href,
      target: attrs.target,
      rel: attrs.rel,
      download: attrs.download,
      disabled: attrs.disabled,
      loading: attrs.loading,
      onclick: attrs.disabled || attrs.loading
        ? undefined
        : (event: MithrilViewEvent<HTMLElement>) => {
          attrs.onclick?.(event as unknown as ButtonClickEvent);
        },
    }, children);
  },
};
